import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { StateContext } from "../../StateContext";
import { kebabize } from "../../helpers/inflection";
import useVenue from "../../hooks/useVenue";
import Beer from "../Beer";
import BoardDivider from "../BoardDivider";
import BoardItem from "../BoardItem";
import Framed from "../Framed";
import { NavLink as Link } from "../TrackedLink";

import * as styles from "./styles.module.scss";

const BeerList = ({ menu }) => {
  const { isBexley, isEltham, venue } = useVenue();
  const menusRef = useRef(null);
  const [rowDividerPositions, setRowDividerPositions] = useState([]);

  const state = useContext(StateContext);
  const menus = useMemo(
    () => state.menus.filter((menu) => menu.venue === venue),
    [state.menus, venue],
  );
  const items = (menu && menu.items) || [];

  // Detect row changes and position dividers
  useEffect(() => {
    if (!isBexley || !menusRef.current) return;

    const detectRows = () => {
      const container = menusRef.current;
      const menuItems =
        container && container.querySelectorAll(`.${styles.menuItem}`);

      if (!menuItems || menuItems.length === 0) return;

      const rows = [];
      let currentRow = [];
      let currentRowTop = menuItems[0].offsetTop;

      menuItems.forEach((item) => {
        if (item.offsetTop !== currentRowTop && currentRow.length > 0) {
          rows.push(currentRow);
          currentRow = [item];
          currentRowTop = item.offsetTop;
        } else {
          currentRow.push(item);
        }
      });

      if (currentRow.length > 0) {
        rows.push(currentRow);
      }

      // Calculate divider positions (between rows)
      const positions = [];
      if (rows.length > 1) {
        for (let i = 0; i < rows.length - 1; i++) {
          const lastItemInRow = rows[i][rows[i].length - 1];
          const bottom = lastItemInRow.offsetTop + lastItemInRow.offsetHeight;
          positions.push(bottom + 1); // Half of the 5px divider height
        }
      }

      setRowDividerPositions(positions);
    };

    detectRows();

    // Re-detect on resize
    const resizeObserver = new ResizeObserver(detectRows);
    resizeObserver.observe(menusRef.current);

    return () => {
      if (menusRef.current) {
        resizeObserver.disconnect();
      }
    };
  }, [menus, isBexley]);

  const renderMenus = () => {
    const location = useLocation();

    return (
      <>
        <BoardDivider />
        <div className={styles.menusContainer}>
          <div className={styles.menus} ref={menusRef}>
            {menus.map((menu, index) => {
              const base = `/${venue.toLowerCase()}/drinks`;
              const to = `${base}/${kebabize(menu.name)}`;
              const isActive =
                location.pathname === to ||
                (location.pathname === base && index === 0);
              return (
                <Link key={menu.name} className={styles.menuItem} to={to}>
                  <BoardItem
                    inverse={isActive}
                    style={"menu"}
                    text={menu.name}
                    large
                  />
                </Link>
              );
            })}
          </div>
          {rowDividerPositions.map((position, index) => (
            <div
              key={`divider-${index}`}
              className={styles.rowDivider}
              style={{ top: `${position}px` }}
            >
              <BoardDivider thin />
            </div>
          ))}
        </div>
        <BoardDivider />
        <div className={styles.menusBottomSpacer} />
      </>
    );
  };

  return (
    <div
      className={classNames({
        [styles.bexley]: isBexley,
        [styles.eltham]: isEltham,
      })}
    >
      <div className={styles.beerList}>
        <Framed fullWidth style={isBexley ? "birch" : "gold"}>
          <div className={styles.beerListContainer}>
            <div className={styles.background} />
            {isBexley && renderMenus()}
            <div className={styles.beers}>
              {items.map((item, index) => (
                <Beer
                  key={item.id}
                  last={index === items.length - 1}
                  {...item}
                />
              ))}
            </div>
          </div>
        </Framed>
      </div>
    </div>
  );
};

BeerList.propTypes = {
  menu: PropTypes.array.isRequired,
};

export default BeerList;
